import { gql } from "graphql-tag";

export const entryFragmentsForExport = gql`
  #
  # ──────────────────────────
  #  VARIABLE FRAGMENTS
  # ──────────────────────────
  #
  # Don't need variable id in entry fields for now as we only inherit one level (from product) currently
  fragment VariableEntryFields on variable_Entry {
    plainText
    inheritIfEmpty
    variableToInherit {
      # only set on scoped variables
      ... on variable_Entry {
        id
      }
    }
    showForTheFollowingProducts {
      ... on product_Entry {
        id
      }
    }
    showForTheFollowingProductFeatures {
      ... on productFeatures_Category {
        id
      }
    }
    showForTheFollowingProductCategories {
      ... on productCategories_Category {
        id
      }
    }
    variableConditions {
      ... on variable_Entry {
        plainText
        inheritIfEmpty
        showForTheFollowingProducts {
          ... on product_Entry {
            id
          }
        }
        showForTheFollowingProductFeatures {
          ... on productFeatures_Category {
            id
          }
        }
        showForTheFollowingProductCategories {
          ... on productCategories_Category {
            id
          }
        }
      }
    }
  }

  #
  # ──────────────────────────
  #  BASIC BLOCK FRAGMENTS
  # ──────────────────────────
  #

  fragment TextBlockFields on textBlock_Entry {
    id
    sortOrder
    typeHandle
    fieldHandle # for determining if owned by a Matrix - matrix field block mutation name is unique
    paragraph {
      rawHtml
    }
    pageBreakAfter
    translationImportTimestamp

    showForTheFollowingProducts {
      id
      title
      typeHandle
    }
    showForTheFollowingProductCategories {
      id
      title
    }
    showForTheFollowingProductFeatures {
      id
      title
    }
    showForThisSiteLanguage
  }

  fragment TextPlusImageBlockFields on textPlusImageBlock_Entry {
    id
    sortOrder
    typeHandle
    fieldHandle # for determining if owned by a Matrix - matrix field block mutation name is unique
    paragraph {
      rawHtml
    }
    image {
      uid
      url
      width
      height
      focalPoint
      hasFocalPoint
    }
    translationImportTimestamp
    showForTheFollowingProducts {
      id
      title
      typeHandle
    }
    showForTheFollowingProductCategories {
      id
      title
    }
    showForTheFollowingProductFeatures {
      id
      title
    }
    showForThisSiteLanguage
  }

  fragment LargeImageBlockFields on largeImageBlock_Entry {
    id
    sortOrder
    typeHandle
    fieldHandle # for determining if owned by a Matrix - matrix field block mutation name is unique
    image {
      uid
      url
      width
      height
      focalPoint
      hasFocalPoint
    }
    translationImportTimestamp
    showForTheFollowingProducts {
      id
      title
      typeHandle
    }
    showForTheFollowingProductCategories {
      id
      title
    }
    showForTheFollowingProductFeatures {
      id
      title
    }
    showForThisSiteLanguage
  }

  fragment CalloutBlockFields on calloutBlock_Entry {
    id
    sortOrder
    typeHandle
    fieldHandle # for determining if owned by a Matrix - matrix field block mutation name is unique
    calloutType
    calloutText
    translationImportTimestamp
    showForTheFollowingProducts {
      id
      title
      typeHandle
    }
    showForTheFollowingProductCategories {
      id
      title
    }
    showForTheFollowingProductFeatures {
      id
      title
    }
    showForThisSiteLanguage
  }

  fragment VideoBlockFields on videoBlock_Entry {
    id
    sortOrder
    typeHandle
    fieldHandle # for determining if owned by a Matrix - matrix field block mutation name is unique
    youtubeVideo
    paragraph {
      rawHtml
    }
    # Some places have "assetVideo { uid url }" instead of "id url"
    # so ensure your fields match usage:
    assetVideo {
      uid
      url
    }
    translationImportTimestamp
    dateUpdated
    showForTheFollowingProducts {
      id
      title
      typeHandle
    }
    showForTheFollowingProductCategories {
      id
      title
    }
    showForTheFollowingProductFeatures {
      id
      title
    }
    showForThisSiteLanguage
  }

  fragment FullWidthImageBlockFields on fullWidthImageBlock_Entry {
    sortOrder
    typeHandle
    fieldHandle # for determining if owned by a Matrix - matrix field block mutation name is unique
    image {
      uid
      url
      width
      height
      focalPoint
      hasFocalPoint
    }
    translationImportTimestamp
    showForTheFollowingProducts {
      id
      title
    }
    showForTheFollowingProductCategories {
      id
      title
    }
    showForTheFollowingProductFeatures {
      id
      title
    }
    showForThisSiteLanguage
  }

  fragment RichTextInlineImagesBlockFields on richTextInlineImagesBlock_Entry {
    id
    sortOrder
    typeHandle
    fieldHandle # for determining if owned by a Matrix - matrix field block mutation name is unique
    richText {
      rawHtml
    }
    showForTheFollowingProducts {
      id
      title
      typeHandle
    }
    showForTheFollowingProductCategories {
      id
      title
    }
    showForTheFollowingProductFeatures {
      id
      title
    }
    showForThisSiteLanguage
  }

  #
  # ────────────────────────────────────
  #  COLUMN-LAYOUT BLOCK FRAGMENTS
  # ────────────────────────────────────
  #

  fragment ColumnComposableBlockFields on columnComposableBlock_Entry {
    id
    typeHandle
    showForTheFollowingProducts {
      id
      title
      typeHandle
    }
    showForTheFollowingProductCategories {
      id
      title
    }
    showForTheFollowingProductFeatures {
      id
      title
    }
    showForThisSiteLanguage
    columnBlockComposableEntries {
      sortOrder
      ... on textBlock_Entry {
        ...TextBlockFields
      }
      ... on largeImageBlock_Entry {
        ...LargeImageBlockFields
      }
      ... on textPlusImageBlock_Entry {
        ...TextPlusImageBlockFields
      }
      ... on richTextInlineImagesBlock_Entry {
        ...RichTextInlineImagesBlockFields
      }
      ... on calloutBlock_Entry {
        ...CalloutBlockFields
      }
      ... on videoBlock_Entry {
        ...VideoBlockFields
      }
    }
  }

  #
  # ───────────────────────────
  #  ARTICLE FRAGMENTS
  # ───────────────────────────
  #

  # Used inside articleWithSteps_Entry’s step builder
  fragment TopicArticleStepBuilderBlockFields on topicArticleStepBuilderBlock_Entry {
    id
    sortOrder
    typeHandle
    showForTheFollowingProducts {
      id
      title
    }
    showForTheFollowingProductCategories {
      id
      title
    }
    showForTheFollowingProductFeatures {
      id
      title
    }
    showForThisSiteLanguage
    step {
      ... on textBlock_Entry {
        ...TextBlockFields
      }
      ... on textPlusImageBlock_Entry {
        ...TextPlusImageBlockFields
      }
      ... on largeImageBlock_Entry {
        ...LargeImageBlockFields
      }
      ... on calloutBlock_Entry {
        ...CalloutBlockFields
      }
      ... on videoBlock_Entry {
        ...VideoBlockFields
      }
      ... on richTextInlineImagesBlock_Entry {
        ...RichTextInlineImagesBlockFields
      }
      ... on columnComposableBlock_Entry {
        ...ColumnComposableBlockFields
      }
    }
  }

  fragment ArticleWithStepsFields on articleWithSteps_Entry {
    id
    title
    userTitle
    typeHandle
    sortOrder
    subtopicGroupTag {
      title
    }
    translationImportTimestamp

    showForTheFollowingProducts {
      id
      title
    }
    showForTheFollowingProductCategories {
      id
      title
    }
    showForTheFollowingProductFeatures {
      id
      title
    }
    showForThisSiteLanguage
    topicArticleStepBuilder {
      ...TopicArticleStepBuilderBlockFields
    }
  }

  fragment ArticleLongformFields on articleLongform_Entry {
    id
    title
    userTitle
    typeHandle
    subtopicGroupTag {
      title
    }
    showForTheFollowingProducts {
      id
      title
      typeHandle
    }
    showForTheFollowingProductCategories {
      id
      title
    }
    showForTheFollowingProductFeatures {
      id
      title
    }
    showForThisSiteLanguage
    translationImportTimestamp

    topicArticleContentBuilder {
      ... on textBlock_Entry {
        ...TextBlockFields
      }
      ... on textPlusImageBlock_Entry {
        ...TextPlusImageBlockFields
      }
      ... on largeImageBlock_Entry {
        ...LargeImageBlockFields
      }
      ... on calloutBlock_Entry {
        ...CalloutBlockFields
      }
      ... on videoBlock_Entry {
        ...VideoBlockFields
      }
      ... on richTextInlineImagesBlock_Entry {
        ...RichTextInlineImagesBlockFields
      }
      ... on columnComposableBlock_Entry {
        ...ColumnComposableBlockFields
      }
    }
  }

  fragment ArticleLongformComposableEntriesFields on articleLongformComposableEntries_Entry {
    id
    title
    userTitle
    typeHandle
    subtopicGroupTag {
      title
    }
    translationImportTimestamp

    showForTheFollowingProducts {
      id
      title
    }
    showForTheFollowingProductCategories {
      id
      title
    }
    showForTheFollowingProductFeatures {
      id
      title
    }
    showForThisSiteLanguage
    topicArticleContentBuilderComposableEntries {
      id
      sortOrder
      sectionHandle
      ... on textBlock_Entry {
        ...TextBlockFields
      }
      ... on textPlusImageBlock_Entry {
        ...TextPlusImageBlockFields
      }
      ... on largeImageBlock_Entry {
        ...LargeImageBlockFields
      }
      ... on calloutBlock_Entry {
        ...CalloutBlockFields
      }
      ... on videoBlock_Entry {
        ...VideoBlockFields
      }
      ... on fullWidthImageBlock_Entry {
        ...FullWidthImageBlockFields
      }
      ... on richTextInlineImagesBlock_Entry {
        ...RichTextInlineImagesBlockFields
      }
      ... on columnComposableBlock_Entry {
        ...ColumnComposableBlockFields
      }
    }
  }

  fragment ArticleStepByStepComposableEntriesFields on articleStepByStepComposableEntries_Entry {
    id
    title
    userTitle
    typeHandle
    translationImportTimestamp
    subtopicGroupTag {
      title
    }
    showForTheFollowingProducts {
      id
      title
      typeHandle
    }
    showForTheFollowingProductCategories {
      id
      title
    }
    showForTheFollowingProductFeatures {
      id
      title
    }
    showForThisSiteLanguage
    stepArticlesComposableEntries {
      id
      sortOrder
      sectionHandle
      ... on articleLongform_Entry {
        ...ArticleLongformFields
      }
      ... on articleLongformComposableEntries_Entry {
        ...ArticleLongformComposableEntriesFields
      }
    }
  }

  # NOTE: Don't forget to expand your block fragments inside any deeper references
  # if you have them. The above "article fragments" already do so.

  #
  # ───────────────────────────
  #  NEST THE BLOCK FRAGMENTS
  # ───────────────────────────
  #
  # Finally, reference block fragments so they're "visible" to everything above
  # Nest block fragments so the above fragments can see them
  # Typically, you just list them at the end:
  # fragmentRefPlaceholder: String
`;

export default entryFragmentsForExport;